import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: IconDefinition;
  color: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo' | 'gray';
  trend?: 'up' | 'down' | 'stable';
  subtitle?: string;
  loading?: boolean;
}

const colorClasses = {
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    border: 'border-blue-200'
  },
  green: {
    bg: 'bg-green-100',
    text: 'text-green-600',
    border: 'border-green-200'
  },
  red: {
    bg: 'bg-red-100',
    text: 'text-red-600',
    border: 'border-red-200'
  },
  yellow: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-600',
    border: 'border-yellow-200'
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-600',
    border: 'border-purple-200'
  },
  indigo: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    border: 'border-indigo-200'
  },
  gray: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-200'
  }
};

export const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  subtitle,
  loading = false
}) => {
  const colorClass = colorClasses[color];

  if (loading) {
    return (
      <div className="bg-white border border-gray-300 rounded p-2 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="h-3 bg-gray-200 rounded mb-2"></div>
            <div className="h-5 bg-gray-200 rounded mb-1"></div>
            <div className="h-2 bg-gray-200 rounded w-2/3"></div>
          </div>
          <div className={`w-8 h-8 ${colorClass.bg} rounded`}></div>
        </div>
      </div>
    );
  }

  const getTrendIcon = () => {
    if (trend === 'up') return '↗';
    if (trend === 'down') return '↘';
    return '→';
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-green-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="bg-white border border-gray-300 rounded p-2">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-xs font-medium text-gray-600 mb-1">{title}</div>
          <div className="text-xl font-bold text-gray-900 mb-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          {subtitle && (
            <div className="text-xs text-gray-500 flex items-center">
              {subtitle}
              {trend && (
                <span className={`ml-2 ${getTrendColor()}`}>
                  {getTrendIcon()}
                </span>
              )}
            </div>
          )}
        </div>
        <div className={`w-8 h-8 ${colorClass.bg} rounded flex items-center justify-center flex-shrink-0`}>
          <FontAwesomeIcon icon={icon} className={`${colorClass.text} text-base`} />
        </div>
      </div>
    </div>
  );
};